import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Bar,
    BarChart,
    ResponsiveContainer,
    Text,
    XAxis,
    YAxis,
} from 'recharts';

import { Flex, Spacer, IconExternal } from '@zillow/constellation';

import { PropertiesSelectors } from 'app/shared/selectors';

import propertyUtils from 'app/shared/utils/propertyUtils';
import numberUtils from 'app/shared/utils/numberUtils';

import {
    StyledTextButtonNoUnderline,
} from 'app/shared/StyledComponents/StyledComponents';

import {
    StyledBarChartContainer,
    StyledStatisticsComponent,
    StyledTitle,
} from './StyledComponents';

// offset for the chart's top margin in order to get the label above the bars to display
const CHART_CONFIG = {
    OFFSET_TOP: 20,
    BAR_GAP: 8,
    BAR_SIZE: 16,
};

// bar layout starts to get funky if less than these values
// real height of the chart is set via css: StyledBarChartContainer
const CHART_HEIGHT = {
    BASE: 100,
};

const CHART_LABEL = {
    INQUIRIES: 'Inquiries',
    VIEWS: 'Views',
};

const BAR_NAME = {
    MY_LISTINGS: 'My Listing',
    COMPARABLES: 'Comparables',
};

const BAR_FILL = {
    PURPLE: '#5b33ad',
    LIGHT_BLUE: '#2bc1e1',
};

const pmrResultsBaseUrl = 'https://www.zillow.com/rental-manager/price-my-rental/results/';

const Statistics = (props) => {
    const {
        city,
        state,
        street,
        zip,
    } = useSelector(reduxState => PropertiesSelectors.makeCurrentProperty(reduxState));

    const { listingStatistics } = props;

    const { stats, similarFloorplans } = listingStatistics;

    // listing
    const {
        startDate: myListingStartDate,
        endDate: myListingEndDate,
        views: myListingViews,
        inquiries: myListingInquiries,
    } = stats;
    const { total: myListingViewsTotal } = myListingViews;
    const { total: myListingInquiriesTotal } = myListingInquiries;

    // comps
    const { stats: compStats } = similarFloorplans;
    const {
        views: compViews,
        inquiries: compInquiries,
    } = compStats;

    // only need to use one set of start and end dates
    // myListingStartDate and compStartDate should be the same
    const title = `Activity for ${myListingStartDate}-${myListingEndDate}`;

    const { total: compViewsTotal } = compViews;
    const { total: compInquiriesTotal } = compInquiries;

    const viewsData = [
        {
            name: CHART_LABEL.VIEWS,
            [BAR_NAME.MY_LISTINGS]: myListingViewsTotal,
            [BAR_NAME.COMPARABLES]: compViewsTotal,
        },
    ];

    const inquiriesData = [
        {
            name: CHART_LABEL.INQUIRIES,
            [BAR_NAME.MY_LISTINGS]: myListingInquiriesTotal,
            [BAR_NAME.COMPARABLES]: compInquiriesTotal,
        },
    ];

    const { formattedKebobCase } = propertyUtils.formatAddress({
        city,
        state,
        street,
        zip,
    });

    const priceMyRentalPath = `${pmrResultsBaseUrl}${formattedKebobCase}/?activeTypes=active `;

    const returnCustomBarLabel = (rechartProps) => {
        const {
            fill,
            label,
            value,
            viewBox,
            y,
        } = rechartProps;

        const { width } = viewBox;
        const calcX = parseInt(width, 10) + 10;

        const response = `${numberUtils.formatWithK(value)} • ${label}`;

        return (
            <Text
                x={calcX}
                y={y}
                dy={12}
                fontSize="12"
                fontFamily="OpenSans, sans-serif"
                fill={fill}
                position="right"
            >
                {response}
            </Text>
        );
    };

    const renderCustomXAxisLabel = (rechartProps) => {
        const { label } = rechartProps;

        return (
            <Text
                x={0}
                y={15}
                dy={0}
                fontSize="16"
                fontFamily="sans-serif"
                position="right"
            >
                {label}
            </Text>
        );
    };

    const getBuffer = (data) => {
        const obj = data[0];
        const maxVal = Math.max(
            obj[BAR_NAME.MY_LISTINGS],
            obj[BAR_NAME.COMPARABLES]
        ).toString().split('');

        let bufferString = '5';
        maxVal.forEach(() => {
            bufferString += '0';
        });

        // remove last digit
        bufferString = bufferString.slice(0, -1);

        return `dataMax + ${bufferString}`;
    };

    const renderBarChart = (data, label) => {
        // validation
        if (!data) {
            console.error('data is required', data);
            return false;
        }

        if (!label) {
            console.error('label is required', label);
            return false;
        }

        // set false to fix label sometimes not displaying on load
        // ref: https://github.com/recharts/recharts/issues/1135
        const isAnimationActive = false;

        return (
            <ResponsiveContainer
                className={`ResponsiveContainer-BarChart ResponsiveContainer-BarChart-${label}`}
                width="100%"
                height={CHART_HEIGHT.BASE}
                debounce={0}
            >
                <BarChart
                    data={data}
                    layout="vertical"
                    margin={{
                        top: CHART_CONFIG.OFFSET_TOP,
                        right: 0,
                        left: 0,
                        bottom: 0,
                    }}
                    barGap={CHART_CONFIG.BAR_GAP}
                    barSize={CHART_CONFIG.BAR_SIZE}
                    stackOffset="sign"
                >
                    <XAxis
                        type="number"
                        hide={false}
                        axisLine={false}
                        tick={false}
                        domain={[0, getBuffer(data)]}
                        label={{ content: renderCustomXAxisLabel, label }}
                    />

                    <YAxis
                        type="category"
                        dataKey="name"
                        hide={true}
                    />

                    <Bar
                        dataKey={BAR_NAME.MY_LISTINGS}
                        fill={BAR_FILL.PURPLE}
                        minPointSize={1}
                        label={{ content: returnCustomBarLabel, label: BAR_NAME.MY_LISTINGS }}
                        isAnimationActive={isAnimationActive}
                    />

                    <Bar
                        dataKey={BAR_NAME.COMPARABLES}
                        fill={BAR_FILL.LIGHT_BLUE}
                        minPointSize={1}
                        label={{ content: returnCustomBarLabel, label: BAR_NAME.COMPARABLES }}
                        isAnimationActive={isAnimationActive}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    return (
        <StyledStatisticsComponent className="Statistics">
            <StyledTitle as="div">{title}</StyledTitle>

            <StyledBarChartContainer className="BarChart-Container">
                {renderBarChart(viewsData, CHART_LABEL.VIEWS)}
                {renderBarChart(inquiriesData, CHART_LABEL.INQUIRIES)}
            </StyledBarChartContainer>

            <StyledTextButtonNoUnderline
                as="a"
                buttonType="primary"
                fontType="body"
                href={priceMyRentalPath}
                target="_blank"
                applyOnHover={true}
            >
                <Flex
                    display="flex"
                    flexDirection="row"
                    flexWrap="nowrap"
                    justifyContent="center"
                    alignItems="center"
                    alignSelf="center"
                >
                    <Spacer marginRight="xs">
                        <span className="TextButton-Content">Compare to my property</span>
                    </Spacer>

                    <Flex display="inline-flex">
                        <IconExternal />
                    </Flex>
                </Flex>
            </StyledTextButtonNoUnderline>
        </StyledStatisticsComponent>
    );
};

Statistics.propTypes = {
    listingStatistics: PropTypes.object.isRequired,
};
Statistics.defaultProps = {};

export default Statistics;
